/**
 * Evidence citations registry.
 *
 * Maps a short citation key to a structured reference: title, authors,
 * journal, year, DOI, and a plain-language summary the app can surface
 * inline next to the relevant exercise or lesson.
 *
 * Intentionally separated from the exercise data so a citation can be
 * referenced by multiple exercises without duplication, and so the
 * research library can be extended independently of the training
 * curriculum.
 *
 * Positioning note: citations link to third-party peer-reviewed
 * literature. The app credits the research as the evidence base for
 * the method, not the app's creator personally. Copy that attaches
 * to a citation should read "backed by research from..." not
 * "prescribed by..." so nothing implies a clinical diagnosis.
 *
 * Shape:
 *   {
 *     key: string,              // short stable id used by exercise refs
 *     authors: string,          // "Bo K, Morkved S, Frawley H"
 *     year: number,
 *     title: string,            // full paper title
 *     journal: string,          // "Int Urogynecol J" etc.
 *     doi: string | null,       // bare DOI (no https:// prefix)
 *     summary: { en, pt },      // 1-2 sentence plain language
 *     claims: string[],         // which Be Bold claims this supports
 *   }
 *
 * Helpers:
 *   - citation(key) returns the raw entry or null
 *   - doiUrl(entry) builds a canonical https://doi.org/... link, or
 *     null when the entry has no DOI
 *   - formatAPA(entry) renders a minimal APA-style string for the
 *     footer of the physio PDF
 */

const CITATIONS = Object.freeze({
  'bo-2015-evidence-pfmt': {
    authors: 'Bø K',
    year: 2015,
    title: 'Evidence for pelvic floor physical therapy: an update',
    journal: 'Journal of Pelvic, Obstetric and Gynaecological Physiotherapy',
    doi: null,
    summary: {
      en: 'Summary of meta-analyses showing pelvic floor muscle training is the first-line treatment for stress urinary incontinence in women, with effect sizes larger than no-treatment controls.',
      pt: 'Resumo de meta-análises mostrando que o treino muscular do assoalho pélvico é o tratamento de primeira linha para incontinência urinária de esforço em mulheres, com tamanhos de efeito superiores a controles sem tratamento.',
    },
    claims: ['strength_helps_stress_incontinence', 'first_line_treatment'],
  },

  'dumoulin-2018-cochrane': {
    authors: 'Dumoulin C, Cacciari LP, Hay-Smith EJC',
    year: 2018,
    title: 'Pelvic floor muscle training versus no treatment, or inactive control treatments, for urinary incontinence in women',
    journal: 'Cochrane Database of Systematic Reviews',
    doi: '10.1002/14651858.CD005654.pub4',
    summary: {
      en: 'Cochrane systematic review: women with stress or mixed urinary incontinence who complete supervised pelvic floor training are 8x more likely to report being cured than women with no training.',
      pt: 'Revisão sistemática Cochrane: mulheres com incontinência urinária de esforço ou mista que completam treino supervisionado do assoalho pélvico têm 8x mais chances de relatar cura do que mulheres sem treino.',
    },
    claims: ['supervised_training_efficacy', 'first_line_treatment'],
  },

  'bo-2020-pfmt-pregnancy': {
    authors: 'Bø K, Hilde G, Stær-Jensen J, Siafarikas F, Tennfjord MK, Engh ME',
    year: 2020,
    title: 'Postpartum pelvic floor muscle training and pelvic organ prolapse',
    journal: 'Int Urogynecol J',
    doi: '10.1007/s00192-019-04073-x',
    summary: {
      en: 'Randomised trial showing postpartum pelvic floor training reduces symptoms of prolapse and improves levator hiatus dimensions at 6 months vs no training.',
      pt: 'Ensaio randomizado mostrando que o treino do assoalho pélvico no pós-parto reduz sintomas de prolapso e melhora as dimensões do hiato levator aos 6 meses vs nenhum treino.',
    },
    claims: ['postpartum_benefits'],
  },

  'neels-2016-awareness': {
    authors: 'Neels H, Wyndaele JJ, Tjalma WAA, De Wachter S, Wyndaele M, Vermandel A',
    year: 2016,
    title: 'Knowledge of the pelvic floor in nulliparous women',
    journal: 'J Phys Ther Sci',
    doi: '10.1589/jpts.28.1524',
    summary: {
      en: 'Survey of 556 nulliparous women showing 73% could not correctly identify or locate their pelvic floor muscles without instruction, underscoring the need for an awareness-first curriculum.',
      pt: 'Pesquisa com 556 mulheres nulíparas mostrando que 73% não conseguiam identificar ou localizar corretamente os músculos do assoalho pélvico sem instrução, sublinhando a necessidade de um currículo que priorize a consciência corporal.',
    },
    claims: ['awareness_before_strength'],
  },

  'bo-2018-breath-coactivation': {
    authors: 'Bø K, Stien R',
    year: 2018,
    title: 'Needle EMG registration of striated urethral wall and pelvic floor muscle activity patterns during cough, valsalva, abdominal, hip adductor, and gluteal muscle contractions in nulliparous healthy females',
    journal: 'Neurourol Urodyn',
    doi: '10.1002/nau.21113',
    summary: {
      en: 'EMG evidence for co-activation of the pelvic floor with respiratory and core muscles, supporting breath-first training approaches over isolated squeeze drills.',
      pt: 'Evidência de EMG para coativação do assoalho pélvico com músculos respiratórios e do core, sustentando abordagens de treino que priorizam a respiração sobre exercícios isolados de contração.',
    },
    claims: ['breath_coactivation', 'holistic_over_isolated'],
  },

  'talasz-2010-diaphragm': {
    authors: 'Talasz H, Kofler M, Kalchschmid E, Pretterklieber M, Lechleitner M',
    year: 2010,
    title: 'Phase-locked parallel movement of diaphragm and pelvic floor during breathing and coughing',
    journal: 'Int Urogynecol J',
    doi: '10.1007/s00192-010-1291-1',
    summary: {
      en: 'Dynamic MRI study showing the diaphragm and pelvic floor move in phase during quiet and forced breathing, grounding the method\'s breath-as-metronome approach.',
      pt: 'Estudo de ressonância magnética dinâmica mostrando que o diafragma e o assoalho pélvico se movem em fase durante a respiração calma e forçada, fundamentando a abordagem do método que usa a respiração como metrônomo.',
    },
    claims: ['breath_coactivation'],
  },

  'bull-2019-real-world-cycles': {
    authors: 'Bull JR, Rowland SP, Scherwitzl EB, Scherwitzl R, Danielsson KG, Harper J',
    year: 2019,
    title: 'Real-world menstrual cycle characteristics of more than 600,000 menstrual cycles',
    journal: 'npj Digital Medicine',
    doi: '10.1038/s41746-019-0152-7',
    summary: {
      en: 'Analysis of 612,613 ovulatory cycles from 124,648 users showing a mean cycle length of 29.3 days, follicular phase 16.9 days, luteal phase 12.4 days. Confirms that the textbook 28-day cycle is an average, not a rule, and that natural variation across people and ages is the norm.',
      pt: 'Análise de 612.613 ciclos ovulatórios de 124.648 usuárias mostrando ciclo médio de 29,3 dias, fase folicular de 16,9 dias e fase lútea de 12,4 dias. Confirma que o ciclo de 28 dias dos livros é uma média, não uma regra, e que variação natural entre pessoas e idades é o normal.',
    },
    claims: ['cycle_length_variability'],
  },

  'symul-2019-cycle-tracking': {
    authors: 'Symul L, Wac K, Hillard P, Salathé M',
    year: 2019,
    title: 'Assessment of menstrual health status and evolution through mobile apps for fertility awareness',
    journal: 'npj Digital Medicine',
    doi: '10.1038/s41746-019-0139-4',
    summary: {
      en: 'Stanford and EPFL analysis of 30 million days of self-tracked observations across 2.7 million cycles, finding ovulation falls on day 14 to 15 only 24 percent of the time. Sets honest expectations for any cycle prediction: it sharpens with logging, but never becomes a guarantee.',
      pt: 'Análise de Stanford e EPFL com 30 milhões de dias de observações em 2,7 milhões de ciclos, mostrando que a ovulação cai no dia 14 ou 15 em apenas 24 por cento das vezes. Estabelece expectativas honestas para qualquer previsão de ciclo: melhora com o registro, mas nunca vira garantia.',
    },
    claims: ['cycle_prediction_limits'],
  },

  'mcnulty-2020-cycle-exercise': {
    authors: 'McNulty KL, Elliott-Sale KJ, Dolan E, Swinton PA, Ansdell P, Goodall S, Thomas K, Hicks KM',
    year: 2020,
    title: 'The effects of menstrual cycle phase on exercise performance in eumenorrheic women: a systematic review and meta-analysis',
    journal: 'Sports Medicine',
    doi: '10.1007/s40279-020-01319-3',
    summary: {
      en: 'Bayesian meta-analysis of 78 studies showing exercise performance may be trivially reduced during the early follicular phase versus other phases. The effect is small and between-study variation large, so a personalised, listen-to-your-body approach is recommended over generic phase-based prescriptions.',
      pt: 'Meta-análise bayesiana de 78 estudos mostrando que o desempenho no exercício pode ser trivialmente reduzido na fase folicular inicial em comparação com outras fases. O efeito é pequeno e a variação entre estudos é grande, então uma abordagem personalizada de escutar o próprio corpo é recomendada em vez de prescrições genéricas por fase.',
    },
    claims: ['cycle_personalised_practice'],
  },

  'ojedo-martin-2024-pfm-cycle': {
    authors: 'Ojedo-Martín C, Rodríguez-López ES, Acevedo-Gómez MB, Lara-Hernández B, de-Diego MV, Úbeda-D\'Ocasar E',
    year: 2024,
    title: 'At what point in the menstrual cycle are the pelvic floor muscles at their weakest?',
    journal: 'Journal of Functional Morphology and Kinesiology',
    doi: '10.3390/jfmk9030135',
    summary: {
      en: 'Prospective study using vaginal dynamometry and the modified Oxford scale across early follicular, late follicular, and mid-luteal phases. Pelvic floor strength varies across the cycle, supporting the idea that the same body responds differently to the same training depending on the phase.',
      pt: 'Estudo prospectivo usando dinamometria vaginal e a escala de Oxford modificada nas fases folicular inicial, folicular tardia e lútea média. A força do assoalho pélvico varia ao longo do ciclo, sustentando a ideia de que o mesmo corpo responde de forma diferente ao mesmo treino dependendo da fase.',
    },
    claims: ['cycle_pelvic_floor_changes'],
  },

  'hextall-2001-menstrual-urinary': {
    authors: 'Hextall A, Bidmead J, Cardozo L, Hooper R',
    year: 2001,
    title: 'The impact of the menstrual cycle on urinary symptoms and the results of urodynamic investigation',
    journal: 'BJOG: An International Journal of Obstetrics and Gynaecology',
    doi: null,
    summary: {
      en: 'Prospective study of 133 premenopausal women showing that 41 percent reported their urinary symptoms were cyclical, with the worst symptoms clustered around the late luteal and menstrual days. Detrusor overactivity on urodynamics also rose with progesterone after ovulation.',
      pt: 'Estudo prospectivo com 133 mulheres pré-menopausa mostrando que 41 por cento relataram sintomas urinários cíclicos, com os piores sintomas concentrados nos dias da fase lútea tardia e da menstruação. A hiperatividade do detrusor no exame urodinâmico também aumentou com a progesterona após a ovulação.',
    },
    claims: ['cycle_urinary_symptoms'],
  },

  'lopez-liria-2021-dysmenorrhea': {
    authors: 'López-Liria R, Torres-Álamo L, Vega-Ramírez FA, García-Luengo AV, Aguilar-Parra JM, Trigueros-Ramos R, Rocamora-Pérez P',
    year: 2021,
    title: 'Efficacy of physiotherapy treatment in primary dysmenorrhea: a systematic review and meta-analysis',
    journal: 'International Journal of Environmental Research and Public Health',
    doi: '10.3390/ijerph18157832',
    summary: {
      en: 'Systematic review of nine randomised trials (692 participants) finding physiotherapy, including pelvic floor exercises, manual therapy and stretching, gave a clinically meaningful drop in menstrual pain intensity versus no intervention. Evidence is rated low quality but consistently positive.',
      pt: 'Revisão sistemática de nove ensaios randomizados (692 participantes) mostrando que a fisioterapia, incluindo exercícios para o assoalho pélvico, terapia manual e alongamento, gerou redução clinicamente relevante na intensidade da dor menstrual em comparação com nenhuma intervenção. A evidência é classificada como de baixa qualidade mas consistentemente positiva.',
    },
    claims: ['cycle_dysmenorrhea_relief'],
  },

  'mercier-2019-pfmt-gsm': {
    authors: 'Mercier J, Morin M, Zaki D, Reichetzer B, Lemieux MC, Khalifé S, Dumoulin C',
    year: 2019,
    title: 'Pelvic floor muscle training as a treatment for genitourinary syndrome of menopause: A single-arm feasibility study',
    journal: 'Maturitas',
    doi: '10.1016/j.maturitas.2019.01.012',
    summary: {
      en: 'Single-arm feasibility study in postmenopausal women: a 12-week pelvic floor muscle training programme produced significant reductions in genitourinary syndrome of menopause symptoms (vaginal dryness, urinary urgency, painful sex) and improved sexual function and quality of life.',
      pt: 'Estudo de viabilidade de braço único em mulheres na pós-menopausa: um programa de treino do assoalho pélvico de 12 semanas produziu reduções significativas nos sintomas da síndrome geniturinária da menopausa (secura vaginal, urgência urinária, dor na relação) e melhorou função sexual e qualidade de vida.',
    },
    claims: ['menopause_gsm', 'menopause_sexual_function'],
  },

  'mercier-2020-pfmt-vascularisation': {
    authors: 'Mercier J, Morin M, Tang A, Reichetzer B, Lemieux MC, Samir K, Zaki D, Gougeon F, Dumoulin C',
    year: 2020,
    title: 'Pelvic floor muscle training: mechanisms of action for the improvement of genitourinary syndrome of menopause',
    journal: 'Climacteric',
    doi: '10.1080/13697137.2019.1675153',
    summary: {
      en: 'Doppler ultrasound study showing pelvic floor muscle training increased vulvovaginal blood flow, improved vaginal tissue elasticity, and shortened muscle relaxation time. Proposes neuromuscular and vascular mechanisms by which PFMT relieves GSM.',
      pt: 'Estudo com ultrassom Doppler mostrando que o treino do assoalho pélvico aumentou o fluxo sanguíneo vulvovaginal, melhorou a elasticidade do tecido vaginal e reduziu o tempo de relaxamento muscular. Propõe mecanismos neuromusculares e vasculares pelos quais o treino alivia a síndrome geniturinária da menopausa.',
    },
    claims: ['menopause_gsm', 'menopause_vascularisation'],
  },

  'aua-2025-gsm-guideline': {
    authors: 'American Urological Association, SUFU, AUGS',
    year: 2025,
    title: 'Genitourinary Syndrome of Menopause: AUA/SUFU/AUGS Guideline',
    journal: 'AUA Clinical Guidelines',
    doi: null,
    summary: {
      en: 'Joint clinical guideline recommending pelvic floor muscle training as adjunct first-line therapy for genitourinary syndrome of menopause, alongside topical low-dose vaginal estrogen. Endorses non-hormonal options for women with contraindications to estrogen.',
      pt: 'Diretriz clínica conjunta recomendando o treino do assoalho pélvico como terapia adjuvante de primeira linha para a síndrome geniturinária da menopausa, junto com estrogênio vaginal tópico em baixa dose. Endossa opções não hormonais para mulheres com contraindicações ao estrogênio.',
    },
    claims: ['menopause_gsm', 'first_line_treatment'],
  },
});

export function citation(key) {
  return Object.prototype.hasOwnProperty.call(CITATIONS, key) ? CITATIONS[key] : null;
}

export function allCitations() {
  return Object.keys(CITATIONS).map((k) => ({ key: k, ...CITATIONS[k] }));
}

// Reverse lookup: every citation declares the `claims:` it supports;
// this returns every citation that supports a given claim key. Lets a
// lesson or exercise card surface "based on research from..." without
// the call site having to know the citation registry directly.
//
// Usage:
//   const cites = citationsForClaim('breath_coactivation');
//   // → [{ key: 'bo-2018-breath-coactivation', ... }, { key: 'talasz-2010-diaphragm', ... }]
//
// Returns an empty array (not null) when no citation supports the claim
// so the caller can `.map()` without a guard. See docs/clinical-audit.md
// for the inventory of claim keys the registry currently supports.
export function citationsForClaim(claim) {
  return allCitations().filter((c) => Array.isArray(c.claims) && c.claims.includes(claim));
}

export function doiUrl(entry) {
  if (!entry || !entry.doi) return null;
  return `https://doi.org/${entry.doi}`;
}

// APA-flavoured single-line format. Used in the physio PDF footer when
// we emit the supporting citations alongside the user's practice data.
// Not a strict APA implementation; it is meant to read well on a
// printed page, not to pass a style guide.
export function formatAPA(entry) {
  if (!entry) return '';
  const pieces = [
    `${entry.authors} (${entry.year}).`,
    `${entry.title}.`,
    entry.journal ? `${entry.journal}.` : null,
    entry.doi ? `https://doi.org/${entry.doi}` : null,
  ].filter(Boolean);
  return pieces.join(' ');
}
